import { useState, useCallback } from 'react';
import inventoryService from '../../../services/inventoryService';
import { usePaginationState } from '../../../hooks/usePaginationState.js';
import { usePaginatedData } from '../../../hooks/usePaginatedData.js';

export const useCategories = (options = {}) => {
    const [actionLoading, setActionLoading] = useState(false);
    
    // Separate UI state from data fetching
    const paginationState = usePaginationState({
        defaultSort: 'name',
        defaultOrder: 'asc',
        defaultLimit: options.defaultLimit || 10,
        limitOptions: options.limitOptions || [10, 20, 50, 100]
    });

    const fetchCategories = useCallback(async (params) => {
        return await inventoryService.getCategories(params);
    }, []);

    const { data, pagination, loading, error, setError, refetch } = usePaginatedData(
        fetchCategories,
        paginationState
    );

    const createCategory = useCallback(async (categoryData) => {
        try {
            setActionLoading(true);
            await inventoryService.createCategory(categoryData);
            refetch(); // Use refetch instead of manual reload
            return { success: true };
        } catch (err) {
            const errorMessage = err.error || err.message || 'Failed to create category';
            setError(errorMessage);
            // Don't refetch on error to avoid clearing the error message
            return { success: false, error: errorMessage };
        } finally {
            setActionLoading(false);
        }
    }, [refetch, setError]);

    const updateCategory = useCallback(async (id, categoryData) => {
        try {
            setActionLoading(true);
            await inventoryService.updateCategory(id, categoryData);
            refetch(); // Use refetch instead of manual reload
            return { success: true };
        } catch (err) {
            const errorMessage = err.error || err.message || 'Failed to update category';
            setError(errorMessage);
            // Don't refetch on error to avoid clearing the error message
            return { success: false, error: errorMessage };
        } finally {
            setActionLoading(false);
        }
    }, [refetch, setError]);

    const deleteCategory = useCallback(async (id) => {
        // Confirmation is now handled by the UI component
        try {
            setActionLoading(true);
            await inventoryService.deleteCategory(id);
            refetch(); // Use refetch instead of manual reload
            return { success: true };
        } catch (err) {
            const errorMessage = err.error || err.message || 'Failed to delete category';
            setError(errorMessage);
            // Don't refetch on error to avoid clearing the error message
            return { success: false, error: errorMessage };
        } finally {
            setActionLoading(false);
        }
    }, [refetch, setError]);

    // Return stable object with memoized properties
    return {
        // Data
        data,
        categories: data, // Alias for backward compatibility
        pagination,
        loading,
        error,
        actionLoading,
        
        // Actions
        createCategory,
        updateCategory,
        deleteCategory,
        setError,
        
        // Pagination controls
        ...paginationState
    };
};